/**
 * Portal root (`/portal` and `/portal/`) — the tenant portal landing.
 *
 * The bare portal URL used to fall through to the operator console (no route
 * matched → the `*` catch-all → `/`). It's the portal's front door now:
 *  - anonymous → the single portal login (ADR-0016);
 *  - signed in with EXACTLY ONE membership → straight into that app's home (a
 *    one-tile launcher is pointless);
 *  - signed in with zero or many → the launcher (`/portal/me`, the app grid).
 *
 * `/portal/me` stays the EXPLICIT launcher (always the list) — this is the
 * smart entry that may skip it.
 */
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Navigate } from "react-router-dom";

import { ApiError } from "@/lib/api";
import { portalApi } from "@/lib/api-portal";
import {
  parentLauncherPath,
  parentLoginPath,
  portalHomePath,
  usePortalAuth,
} from "@/lib/portalAuth";

export function PortalRoot() {
  const { token, logout } = usePortalAuth();

  const appsQ = useQuery({
    queryKey: ["portal", "my-apps", "root"],
    queryFn: () => portalApi.myApps(),
    enabled: !!token,
    retry: false,
  });

  // Anonymous → the single portal login door.
  if (!token) return <Navigate to={parentLoginPath()} replace />;

  // A stale/expired session (401) on the apps probe → drop it and re-login.
  if (appsQ.error) {
    if (appsQ.error instanceof ApiError && appsQ.error.status === 401) logout();
    return <Navigate to={parentLoginPath()} replace />;
  }

  if (appsQ.isPending) {
    return (
      <div className="tray-light flex min-h-screen items-center justify-center bg-background text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  const apps = appsQ.data ?? [];
  // Exactly one membership → enter that app (PortalAppEntry resolves its home).
  if (apps.length === 1) return <Navigate to={portalHomePath(apps[0].slug)} replace />;
  // Zero or many → the launcher (the app grid; an empty grid is a real state).
  return <Navigate to={parentLauncherPath()} replace />;
}
